<template>
  <div class="home-page">
    <div
      v-for="(tile, index) in tiles || []"
      :key="tile.id"
      class="tiles"
      :data-tile-id="tile.id"
      :data-tile-position="index"
      :data-tile-type="tile.type"
      :data-tile-usage-type="getTileUsageType(tile)"
    >
      <template v-if="isNotPopup(tile)">
        <div class="container unpadded">
          <ImageTile
            v-if="tile.actionType !== 'REASONS_TO_BUY' || !user || !user.isTrade"
            :action-type="tile.actionType"
            :desktop-image="tile.desktopImage"
            :mobile-image="tile.mobileImage"
            :type="tile.type"
            :url="tile.url"
          />
        </div>
      </template>
      <template v-else-if="tile.type === 'SETTING_SHOTS_CAROUSEL'">
        <div class="clipper" v-if="mounted">
          <Lazy :delay="500">
            <div class="container carousel" style="padding-bottom: 40px">
              <suspense>
                <ShopByWidget :title="tile.displayTitle" />
              </suspense>
            </div>
          </Lazy>
        </div>
      </template>
      <template v-else-if="tile.type === 'RECOMMENDER'">
        <div class="clipper">
          <Lazy>
            <suspense>
              <Recommender :title="tile.displayTitle" />
            </suspense>
          </Lazy>
        </div>
      </template>
      <template v-else-if="tile.type === 'PRODUCT_CAROUSEL'">
        <div class="clipper" v-if="mounted">
          <Lazy>
            <div class="container carousel">
              <suspense>
                <ProductCarousel
                  :title="tile.displayTitle"
                  :products="carouselProducts[tile.id].products"
                  :on-sale-filter="carouselProducts[tile.id].filters.onSale"
                  :to-send-event="true"
                  :swiper-margin="true"
                />
              </suspense>
            </div>
          </Lazy>
        </div>
      </template>

      <template v-else-if="tile.type === 'NEWSLETTER_WIDGET'">
        <div class="container unpadded">
          <Lazy :delay="1000">
            <suspense>
              <NewsletterWithPicture
                :title="tile.displayTitle"
                :bg-image="tile.desktopImage"
                :bg-image-mobile="tile.mobileImage"
                :email-form-center="true"
              />
            </suspense>
          </Lazy>
        </div>
      </template>

      <template v-else-if="isPopup(tile)">
        <div class="container unpadded">
          <suspense>
            <Popup
              :action-type="tile.actionType"
              :desktop-image="tile.desktopImage"
              :mobile-image="tile.mobileImage"
              :type="tile.type"
              :url="tile.url"
              :products="tile.products"
            />
          </suspense>
        </div>
      </template>

      <template v-else-if="tile.type === 'ANIMATION'">
        <div class="container unpadded">
          <Lazy :delay="1000">
            <suspense>
              <AnimationTile
                :action-type="tile.actionType"
                :desktop-image="tile.desktopImage"
                :mobile-image="tile.mobileImage"
                :desktop-animation="tile.desktopAnimation"
                :mobile-animation="tile.mobileAnimation"
                :type="tile.type"
                :url="tile.url"
              />
            </suspense>
          </Lazy>
        </div>
      </template>
    </div>

    <div class="container top-spacer">
      <Lazy :delay="1000">
        <suspense>
          <Reviews />
        </suspense>
      </Lazy>
    </div>

    <div class="clipper" v-if="ugcPixleeEnabled">
      <Lazy :delay="1000">
        <div class="container carousel top-spacer">
          <suspense>
            <HomeUGC></HomeUGC>
          </suspense>
        </div>
      </Lazy>
    </div>

    <div class="clipper top-spacer bottom-spacer">
      <div class="container nested-content">
        <Lazy :delay="1000">
          <suspense>
            <Press />
          </suspense>
        </Lazy>
      </div>
    </div>
    <Newsletter />

    <suspense>
      <DiscountPopup v-if="discount" />
    </suspense>
    <suspense>
      <CanFavorite v-if="mounted" />
    </suspense>
    <suspense>
      <ProductListModal v-if="mounted" @update:show="updateShowProductListModal($event)"></ProductListModal>
    </suspense>
  </div>
</template>

<script>
import { Lazy, MediaUtils } from '@/pixel/index';
import { debounce } from '@/components/commons/MiniLodash';
import StructuredOrganization from '@/components/meta/StructuredOrganization.js';
import buildSocialMetaTags from '@/components/meta/Social.js';
import ImageTile from '@/components/home/ImageTile.vue';
import LocalStorage, { LocalStorageKeys } from '@/components/commons/LocalStorage';
import { computed, defineAsyncComponent, onBeforeMount, onMounted } from 'vue';
import useHome from '@/compositions/useHome';
import useCommon from '@/compositions/useCommon';
import { useRouter } from 'vue-router';
import { ref } from 'vue';
import useHead from '@/compositions/useHead';
import Newsletter from '@/components/commons/Newsletter.vue';
import useDiscount from '@/compositions/useDiscount';

export default {
  name: 'HomePage',

  components: {
    Lazy,
    ImageTile,
    Newsletter,
    AnimationTile: defineAsyncComponent(() => import('@/components/home/AnimationTile.vue')),
    Recommender: defineAsyncComponent(() => import('@/components/commons/RecommenderNA.vue')),
    ProductCarousel: defineAsyncComponent(() => import('@/components/commons/ProductCarousel.vue')),
    HomeUGC: defineAsyncComponent(() => import('@/components/ugc/HomeUGC.vue')),
    NewsletterWithPicture: defineAsyncComponent(() => import('@/components/commons/NewsletterWithPicture.vue')),
    Reviews: defineAsyncComponent(() => import('@/components/home/Reviews.vue')),
    Press: defineAsyncComponent(() => import('@/components/home/Press.vue')),
    Popup: defineAsyncComponent(() => import('@/components/home/Popup.vue')),
    DiscountPopup: defineAsyncComponent(() => import('@/components/home/DiscountPopup.vue')),
    CanFavorite: defineAsyncComponent(() => import('@/components/shared/CanFavorite.vue')),
    ShopByWidget: defineAsyncComponent(() => import('@/components/commons/ShopByWidget/components/ShopByWidget.vue')),
    ProductListModal: defineAsyncComponent(() => import('@/components/commons/ShopByWidget/components/ProductListModal.vue')),
  },

  async setup() {
    const router = useRouter();

    const { user, ugcPixleeEnabled } = useCommon();

    const { carouselProducts, reviews, tiles, title, description, fetchData, initInteriorDesignExp, updateShowProductListModal } = useHome();

    const { discount, fetchUrlDiscount, saveCustomerDiscount } = useDiscount();

    const metaTags = computed(() => [
      {
        name: 'description',
        content:
          'Great style is easy. Article offers stylish modern, mid century, and scandinavian furniture from world renowned designers at accessible prices. Shop now.',
      },
      ...buildSocialMetaTags({
        title: title.value,
        description: description.value,
        url: import.meta.env.VITE_STOREFRONT_BASE_URL,
        image: tiles.value && tiles.value.length && tiles.value[0].desktopImage ? tiles.value[0].desktopImage : null,
      }),
    ]);

    useHead(
      {
        title: title.value,
        script: [{ type: 'application/ld+json', children: JSON.stringify(StructuredOrganization) }],
        meta: computed(() => metaTags.value),
      },
      { appendTitleSuffix: false },
    );

    const mounted = ref(false);

    onBeforeMount(() => {
      addTilesAttributes();

      window.setTimeout(() => {
        initInteriorDesignExp();
      }, 1000);
    });

    onMounted(() => {
      mounted.value = true;
      window.addEventListener('resize', debounce(addTilesAttributes, 500));

      if (router.currentRoute.value.params?.discount) {
        if (discount.value) {
          LocalStorage.set(LocalStorageKeys.DISCOUNT_CODE, router.currentRoute.value.params.discount);
        } else {
          window.location.href = '/404';
        }
      }
    });

    if (router.currentRoute.value.params?.discount) {
      await fetchUrlDiscount({
        discountName: router.currentRoute.value.params.discount,
      });

      if (discount.value) {
        await saveCustomerDiscount();
      } else {
        showNotFoundPage({ reason: `Invalid url discount ${router.currentRoute.value.params.discount}` });
      }
    }

    const { id, slug } = router.currentRoute.value.params ?? {};
    if (id && slug) {
      await fetchData({
        pageId: id,
        slug,
      });
    } else {
      await fetchData();
    }

    function isNotPopup(tile) {
      const { type, actionType } = tile;
      return (type === 'FULL_WIDTH_IMAGE' || type === 'LOOKBOOK_IMAGE') && actionType !== 'POPUP' && actionType !== 'POPUP_PRODUCTS';
    }

    function isPopup(tile) {
      try {
        if (tile && tile.actionType) {
          return tile.actionType.includes('POPUP');
        }
      } catch (error) {
        console.error(error);
      }

      return false;
    }

    function addTilesAttributes() {
      const calcSize = (width, height) => {
        const ratio = Math.round((height / width) * 100);
        let ratioAlias = '';
        if (MediaUtils.isMobileAndGreater) {
          if (ratio > 40) {
            ratioAlias = 'big';
          } else if (ratio > 25) {
            ratioAlias = 'medium';
          } else {
            ratioAlias = 'small';
          }
        } else if (ratio > 90) {
          ratioAlias = 'big';
        } else if (ratio > 70) {
          ratioAlias = 'medium';
        } else {
          ratioAlias = 'small';
        }

        return [ratioAlias, ratio];
      };

      const tilesDoms = document.querySelectorAll('.tiles');
      if (tilesDoms && tilesDoms.length) {
        [...tilesDoms].forEach((tile) => {
          const { clientWidth, clientHeight } = tile;
          const tileSizeTitle = MediaUtils.isMobileAndGreater ? 'data-tile-size-desktop' : 'data-tile-size-mobile';
          const tileSizeTitleRatio = MediaUtils.isMobileAndGreater ? 'data-tile-size-desktop-ratio' : 'data-tile-size-mobile-ratio';
          const [ratioAlias, ratio] = calcSize(clientWidth, clientHeight);
          tile.setAttribute(tileSizeTitle, ratioAlias);
          tile.setAttribute(tileSizeTitleRatio, ratio);
        });
      }
    }

    function getTileUsageType(tile) {
      // UGC should be here too when it's configurable
      if (tile.type === 'PRODUCT_CAROUSEL') {
        return 'PRODUCT_CAROUSEL';
      }
      if (tile.type === 'SETTING_SHOTS_CAROUSEL') {
        return 'SETTING_SHOTS_CAROUSEL';
      }
      if (tile.type === 'NEWSLETTER_WIDGET') {
        return 'NEWSLETTER_WIDGET';
      }
      if (tile.actionType === 'POPUP' && tile.url && tile.url.includes('/story/')) {
        return 'POPUP_STORY';
      }
      if (tile.actionType === 'POPUP') {
        return 'POPUP';
      }
      if (tile.actionType === 'POPUP_PRODUCTS') {
        return 'POPUP_PRODUCTS';
      }
      if (tile.actionType === 'REASONS_TO_BUY') {
        return 'REASONS_TO_BUY';
      }
      if (tile.url) {
        return getUrlType(tile.url);
      }
      return 'STATIC';
    }

    function getUrlType(url) {
      if (url.includes('/browse/')) {
        return 'LINK_BROWSE';
      }
      if (url.includes('/promotion/')) {
        return 'LINK_PROMOTION';
      }
      if (url.includes('/product/')) {
        return 'LINK_PRODUCT';
      }
      if (url.includes('/l/')) {
        return 'LINK_LANDING';
      }
      if (url.includes('/collection/')) {
        return 'LINK_COLLECTION';
      }
      if (url.includes('/tag/')) {
        return 'LINK_TAG';
      }
      if (url.includes('/sale')) {
        return 'LINK_SALE';
      }
      if (url.includes('/lookbook/')) {
        return 'LINK_LOOKBOOK';
      }
      if (url.includes('/clearance')) {
        return 'LINK_CLEARANCE';
      }
      return 'LINK';
    }

    return {
      user, //
      carouselProducts,
      reviews,
      discount,
      tiles,
      ugcPixleeEnabled,
      isNotPopup,
      isPopup,
      getTileUsageType,
      mounted,
      updateShowProductListModal,
    };
  },
};
</script>

<style lang="postcss">
@import '@/pixel/style/variables';
@import '@/styles/information';

.home-page {
  width: 100%;
  background: white;

  .shop-by-widgets-container {
    margin: 30px 0;
  }

  .lottie-container {
    .lottie-player {
      position: relative;
      overflow: hidden;
      svg {
        position: absolute;
        top: 0px;
      }
    }
  }
}
</style>
